/**
 * Centralized auth-token storage.
 *
 * Why a wrapper instead of calling localStorage everywhere:
 *   1. Single source of truth — easier to swap localStorage → sessionStorage
 *      → httpOnly cookies later without combing the codebase.
 *   2. Defensive: ignores SSR/disabled-storage edge cases; never throws.
 *   3. Token validation — strips obviously-bad values (empty, non-string,
 *      whitespace) so the Authorization header is never `Bearer null`.
 *   4. One place to log auth-flow events if we need to debug a session bug.
 *
 * SECURITY NOTE: We use localStorage for the JWT because the Emergent ingress
 * sets `Access-Control-Allow-Origin: *` which conflicts with cookie-based auth
 * (see /app/frontend/src/lib/api.js for the long-form rationale). To mitigate
 * XSS risk we (a) sanitise all user-rendered content via React's default
 * escaping, (b) never `dangerouslySetInnerHTML` user data, (c) keep CSP
 * defaults from CRA. Moving to httpOnly cookies requires ingress changes that
 * are out of scope here — tracked in PRD.md backlog.
 */

const TOKEN_KEY = "fdp_access_token";

function _safe() {
  try {
    // Storage can throw in SSR, privacy-mode Safari, or sandboxed iframes.
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

function _isUsableToken(v) {
  return typeof v === "string" && v.length > 10 && v !== "null" && v !== "undefined";
}

export function getAccessToken() {
  const s = _safe();
  if (!s) return "";
  try {
    const raw = s.getItem(TOKEN_KEY);
    return _isUsableToken(raw) ? raw : "";
  } catch {
    return "";
  }
}

export function setAccessToken(token) {
  const s = _safe();
  if (!s) return;
  try {
    if (_isUsableToken(token)) {
      s.setItem(TOKEN_KEY, token);
    } else {
      s.removeItem(TOKEN_KEY);
    }
  } catch (e) {
    // Quota exceeded / privacy mode — log so the engineer sees it in devtools.
    // We don't fail the auth flow; downstream `api` calls will just hit 401.
    // eslint-disable-next-line no-console
    console.warn("setAccessToken failed:", e?.message || e);
  }
}

export function clearAccessToken() {
  setAccessToken(null);
}

export function clearAuthState() {
  clearAccessToken();
  // Also clear any legacy demo-mode flag persisted by earlier versions
  // — left over keys in localStorage shouldn't survive a logout.
  const s = _safe();
  if (s) {
    // Best-effort cleanup of a legacy demo-mode flag. removeItem can throw in
    // very locked-down browsers (Safari private mode, storage-disabled
    // contexts); we surface the failure at debug level only because it never
    // blocks the logout flow.
    try { s.removeItem("fdp_demo_mode"); } catch (e) {
      if (typeof console !== "undefined") {
        console.debug("clearAuthState: legacy key removal skipped", e?.message || e);
      }
    }
  }
}


// --- Sprint 1 Vision AI opt-in banner dismissal (per-browser preference) ---
const VISION_BANNER_KEY = "fdp_vision_banner_dismissed";

export function getVisionBannerDismissed() {
  const s = _safe();
  if (!s) return false;
  try {
    return s.getItem(VISION_BANNER_KEY) === "1";
  } catch {
    return false;
  }
}

export function setVisionBannerDismissed(v) {
  const s = _safe();
  if (!s) return;
  try {
    if (v) s.setItem(VISION_BANNER_KEY, "1");
    else s.removeItem(VISION_BANNER_KEY);
  } catch (e) {
    if (typeof console !== "undefined") {
      console.debug("setVisionBannerDismissed skipped", e?.message || e);
    }
  }
}
